import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { api, errMsg } from '../api';
import { Card, Spinner, ghostBtn, ReadMore } from '../ui';
import { usePortal } from '../PortalAuth';
import { LEVELS, ScoreBadge, scoreColor } from '../travel/TravelDashboard';
import { LEVEL_COLORS, NONE, TFlag, LevelChip } from '../travel/parts';
import RiskBreakdown from '../travel/RiskBreakdown';

const PILLARS = { conflict: 'Conflitti', crime: 'Criminalità', health: 'Salute', governance: 'Governance', environment: 'Ambiente' };
const prose = 'text-sm text-[#C9CCD1] leading-relaxed [&_p]:mb-2 [&_a]:text-[#D4AF37] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_strong]:text-current [&_strong]:font-semibold [&_b]:text-current [&_b]:font-semibold [&_span]:text-current [&_font]:text-current';

function Hero({ d, iso3 }) {
  const lvl = d.farnesina?.level;
  const col = LEVEL_COLORS[lvl] || NONE;
  return (
    <Card className="p-0 overflow-hidden mb-4" data-testid="travel-country-hero">
      <div className="h-1.5" style={{ background: col }} />
      <div className="p-6 flex flex-wrap items-center gap-5">
        <TFlag iso2={d.iso2} className="w-16 h-11" />
        <div className="flex-1 min-w-[200px]">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#8A8F97] mb-1">Scheda Paese · {iso3}</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#F2F2F2]" data-testid="travel-country-name">{d.name || iso3}</h1>
        </div>
        <div className="flex flex-col items-start gap-2">
          <LevelChip level={lvl} />
          <span className="flex items-center gap-2 text-xs text-[#8A8F97]">Indice BOND <ScoreBadge score={d.index?.score == null ? null : Math.round(d.index.score)} /></span>
        </div>
      </div>
    </Card>
  );
}

const fmt = (s) => { const t = s && new Date(s); return t && !isNaN(t) ? t.toLocaleDateString('it-IT') : null; };
const fmtT = (s) => (s ? new Date(s).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : null);

function Dates({ updated, checked, id }) {
  return (
    <p className="text-[11px] text-[#5A5F67] mt-3" data-testid={`travel-dates-${id}`}>
      {fmt(updated) ? `Aggiornata dalla fonte il ${fmt(updated)}` : 'Data della fonte non indicata'}{checked ? ` · verificata da BOND il ${fmtT(checked)}` : ''}
    </p>
  );
}

function UsCard({ g, checked }) {
  return (
    <Card className="p-6" data-testid="travel-us-card">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-2">U.S. Department of State</p>
      {!g ? <p className="text-sm text-[#8A8F97]">Nessun avviso pubblicato per questo Paese.</p> : (
        <>
          <p className="font-heading text-lg text-[#F2F2F2] mb-3" data-testid="travel-us-level">Livello {g.level}/4 · {g.text}</p>
          {g.summary && <div className={`${prose} max-h-72 overflow-y-auto`} dangerouslySetInnerHTML={{ __html: g.summary }} />}
          <a href={g.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#D4AF37] mt-3 hover:underline" data-testid="travel-us-link">Avviso ufficiale (in inglese)<ExternalLink size={11} /></a>
          <Dates updated={g.updated} checked={checked} id="us" />
        </>
      )}
    </Card>
  );
}

const SHORT = { farnesina: 'Italia', us: 'USA', ca: 'Canada', uk: 'Regno Unito', de: 'Germania', il: 'Israele', jp: 'Giappone', nz: 'Nuova Zelanda' };

function SourceChart({ d }) {
  const src = d.sources || {};
  const mean = d.index?.level;
  return (
    <Card className="p-6" data-testid="travel-source-chart">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-1">Fonti a confronto</p>
      <p className="text-xs text-[#8A8F97] mb-4">Livello di rischio 1-4 per fonte · la linea indica la media BOND{mean ? ` (${mean.toFixed(1)})` : ''}.</p>
      <div className="relative">
        {Object.entries(src).map(([k, name]) => {
          const lv = k === 'farnesina' ? d.farnesina?.level : (d.gov || {})[k]?.level;
          return (
            <div key={k} className="grid grid-cols-[110px_1fr_28px] sm:grid-cols-[150px_1fr_28px] gap-3 items-center my-2 text-xs" data-testid={`chart-row-${k}`}>
              <span className="text-[#C9CCD1] truncate" title={name}>{SHORT[k] || name}</span>
              <div className="h-3 bg-[#1F242C] rounded-sm relative">
                {lv && <div className="h-full rounded-sm transition-[width] duration-700" style={{ width: `${lv * 25}%`, background: LEVEL_COLORS[lv] }} />}
              </div>
              <span className="text-right text-[#F2F2F2]" data-testid={`chart-level-${k}`}>{lv || 'n.d.'}</span>
            </div>
          );
        })}
        {mean && (
          <div className="absolute inset-y-0 pointer-events-none grid grid-cols-[110px_1fr_28px] sm:grid-cols-[150px_1fr_28px] gap-3 w-full">
            <span /><div className="relative"><i className="absolute inset-y-0 border-l-2 border-dashed border-[#D4AF37]" style={{ left: `${mean * 25}%` }} data-testid="chart-mean-line" /></div><span />
          </div>
        )}
      </div>
      <div className="flex justify-between text-[10px] text-[#5A5F67] mt-2 pl-[122px] sm:pl-[162px] pr-10"><span>0</span><span>1</span><span>2</span><span>3</span><span>4</span></div>
    </Card>
  );
}

function IndexCard({ d }) {
  const idx = d.index || {};
  const src = d.sources || {};
  return (
    <Card className="p-6" data-testid="travel-score-card">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-3">Indice BOND</p>
      <div className="flex items-center gap-3 mb-3"><ScoreBadge score={idx.score == null ? null : Math.round(idx.score)} /><span className="text-xs text-[#8A8F97]">su 10 · più alto = più sicuro</span></div>
      <p className="text-xs text-[#8A8F97] mb-3" data-testid="travel-index-note">Media, con lo stesso peso, delle {idx.count || 0} fonti istituzionali disponibili per questo Paese (su {idx.total || 8}), ricondotte a una scala comune 1-4.</p>
      {Object.entries(src).map(([k, name]) => {
        const g = k === 'farnesina' ? { ...d.farnesina, text: d.farnesina?.label } : (d.gov || {})[k];
        return (
          <a key={k} href={g?.url} target="_blank" rel="noreferrer" className="block py-2 border-t border-[#1F242C] text-xs hover:text-[#D4AF37]" data-testid={`travel-gov-${k}`}>
            <span className="flex justify-between gap-3"><span className="text-[#F2F2F2]">{name}</span><span className="text-[#C9CCD1] text-right shrink-0">{g?.level ? `Livello ${g.level}/4` : 'n.d.'}</span></span>
            {g?.level && <span className="flex justify-between gap-3 text-[#8A8F97] mt-0.5"><span>{g.text}{g.note ? ` · ${g.note}` : ''}</span><span className="shrink-0">{fmt(g.updated) || ''}</span></span>}
          </a>
        );
      })}
      <p className="text-[11px] text-[#5A5F67] mt-3" data-testid="travel-frequency">Frequenza di aggiornamento: {d.frequency}.{d.checked ? ` Ultima verifica avvisi esteri: ${fmtT(d.checked)}.` : ''}</p>
    </Card>
  );
}

function Side({ d }) {
  const fa = d.farnesina || {};
  return (
    <div className="space-y-4">
      <Card className="p-6" data-testid="travel-farnesina-card">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-2">Indicazione Farnesina</p>
        <p className="font-heading text-lg text-[#F2F2F2] mb-3" data-testid="travel-farnesina-level">{fa.label}</p>
        {fa.recommendations && <div className={`${prose} max-h-72 overflow-y-auto`} dangerouslySetInnerHTML={{ __html: fa.recommendations }} />}
        <Dates updated={fa.updated} checked={d.farnesina_checked} id="farnesina" />
      </Card>
      <UsCard g={(d.gov || {}).us} checked={d.checked} />
      <SourceChart d={d} />
      <IndexCard d={d} />
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function LegacySide({ d }) {
  const adv = Object.values(d.advisories || {});
  return (
    <div className="space-y-4">
      <Card className="p-6" data-testid="travel-score-card">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-3">Indice di sicurezza</p>
        <div className="flex items-center gap-3 mb-4"><ScoreBadge score={d.scoreDisplay} /><span className="text-xs text-[#8A8F97]">su 10 · più alto = più sicuro</span></div>
        {Object.entries(d.pillars || {}).map(([k, v]) => (
          <div key={k} className="grid grid-cols-[90px_1fr_32px] gap-2 items-center text-xs my-1.5">
            <span className="text-[#8A8F97]">{PILLARS[k] || k}</span>
            <div className="h-1.5 bg-[#1F242C] rounded"><div className="h-full rounded" style={{ width: `${v * 100}%`, background: scoreColor(v * 10) }} /></div>
            <span className="text-[#F2F2F2] text-right">{Math.round(v * 10)}</span>
          </div>
        ))}
      </Card>
      {adv.length > 0 && (
        <Card className="p-6" data-testid="travel-advisories">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-3">Avvisi dei governi esteri</p>
          {adv.map((a) => (
            <a key={a.source} href={a.url} target="_blank" rel="noreferrer" className="flex justify-between gap-3 py-2 border-t border-[#1F242C] first:border-0 text-xs hover:text-[#D4AF37]">
              <span className="text-[#F2F2F2]">{a.source}</span><span className="text-[#8A8F97] text-right">{LEVELS[a.level] || a.text}<ExternalLink size={10} className="inline ml-1" /></span>
            </a>
          ))}
        </Card>
      )}
    </div>
  );
}

export default function TravelCountryPage() {
  const { iso3 } = useParams();
  const { user } = usePortal();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { setErr(''); if (user.travel_enabled) api.get(`/travel/countries/${iso3}`).then((r) => setD(r.data)).catch((e) => { if (e.response?.status === 404) setErr(errMsg(e)); else toast.error(errMsg(e)); }); }, [iso3, user.travel_enabled]);
  if (!user.travel_enabled) return <Navigate to="/portal/travel-security" replace />;
  if (err) return <Card className="p-8 max-w-xl" data-testid="travel-country-unavailable"><p className="text-base mb-2">Scheda Paese non disponibile</p><p className="text-sm text-[#A3A3A3] mb-5">{err}</p><Link to="/portal/travel-security" className={`${ghostBtn} h-10 px-4 inline-flex items-center gap-2 text-sm`}><ArrowLeft size={15} />Tutti i Paesi</Link></Card>;
  if (!d) return <Spinner label="Caricamento scheda Paese…" />;
  return (
    <div data-testid="travel-country-page">
      <div className="flex justify-end mb-4">
        <Link to="/portal/travel-security" className={`${ghostBtn} h-10 px-4 flex items-center gap-2 text-sm`} data-testid="travel-back"><ArrowLeft size={15} />Tutti i Paesi</Link>
      </div>
      <Hero d={d} iso3={iso3} />
      {d.news?.length > 0 && (
        <Card className="p-6 mb-4 border-[#C2423A]/50" data-testid="travel-country-news">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#E37A72] mb-3">Ultima ora</p>
          {d.news.map((n) => <div key={n.id} className="mb-3"><p className="text-sm font-semibold text-[#F2F2F2]">{n.title}</p><ReadMore clamp="max-sm:line-clamp-5" testid={`travel-country-news-${n.id}`}><div className={prose} dangerouslySetInnerHTML={{ __html: n.text }} /></ReadMore></div>)}
        </Card>
      )}
      <RiskBreakdown ins={d.insights} />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-4 items-start">
        <Card className="p-6 min-w-0">
          <Accordion type="multiple" defaultValue={[d.sections?.[0]?.id]} data-testid="travel-sections">
            {(d.sections || []).map((s) => (
              <AccordionItem key={s.id} value={s.id} className="border-[#1F242C]">
                <AccordionTrigger className="text-[#F2F2F2] font-heading text-lg" data-testid={`travel-section-${s.id}`}>{s.title}</AccordionTrigger>
                <AccordionContent>{s.items.map((it) => <div key={it.title} className="mb-4"><p className="text-xs uppercase tracking-wider text-[#D4AF37] mb-1">{it.title}</p><div className={prose} dangerouslySetInnerHTML={{ __html: it.html }} /></div>)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="text-xs text-[#5A5F67] mt-4">Fonte: Viaggiare Sicuri (Farnesina){d.updated ? ` · aggiornata al ${new Date(d.updated).toLocaleDateString('it-IT')}` : ''}.</p>
        </Card>
        <Side d={d} />
      </div>
    </div>
  );
}
